// ModelCatalogAdminPage — /admin/model-catalog
//
// Three sections:
//   1. Status panel        — last weekly cron run (Firestore system_status/modelCatalogSync)
//   2. Run sync now        — admin-callable runModelCatalogSyncOnDemand, dryRun=false
//   3. Recent additions    — fetches each provider's llm-catalog/providers/<id>.json from
//                            mchatai-source raw GitHub, flattens, sorts by addedAt desc
//
// Same auth pattern as TemplateCandidatesAdminPage: UID allowlist OR custom claim role=admin.
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './App.css';

const ADMIN_UIDS = ['GI1GHQ7wpLQKjJyf9QRQATyw5e33', 'vGB6BL3iJfh32aWZ0HA93nUV2013'];

const PROVIDERS = ['anthropic', 'openai', 'google', 'mistral', 'deepseek', 'xai', 'openrouter'];
const RAW_BASE = 'https://raw.githubusercontent.com/LawrenceNorman/mchatai-source/main/llm-catalog/providers';

export default function ModelCatalogAdminPage() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);

  const [catalogs, setCatalogs] = useState({});      // { providerID: { models: [...] } }
  const [catalogsLoading, setCatalogsLoading] = useState(true);

  const [running, setRunning] = useState(false);
  const [lastRunResult, setLastRunResult] = useState(null);
  const [runError, setRunError] = useState(null);

  // Phase RL — manual retire (route-lint review table)
  const [retiringKey, setRetiringKey] = useState(null);
  const [retiredKeys, setRetiredKeys] = useState(() => new Set());
  const [retireError, setRetireError] = useState(null);

  const [windowDays, setWindowDays] = useState(30);
  const [providerFilter, setProviderFilter] = useState('all');

  // Auth
  useEffect(() => {
    const auth = getAuth();
    const unsub = auth.onAuthStateChanged(async (u) => {
      setUser(u);
      if (u && (ADMIN_UIDS.includes(u.uid) || (await u.getIdTokenResult()).claims?.role === 'admin')) {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }
      setAuthLoading(false);
    });
    return () => unsub();
  }, []);

  // Status doc
  const reloadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const db = getFirestore();
      const snap = await getDoc(doc(db, 'system_status', 'modelCatalogSync'));
      setStatus(snap.exists() ? snap.data() : null);
    } catch (err) {
      console.warn('[ModelCatalogAdmin] status load failed:', err.message);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  // Catalogs — fetch all provider JSONs in parallel. Cache-bust on manual reload.
  const reloadCatalogs = useCallback(async (cacheBust = false) => {
    setCatalogsLoading(true);
    const qs = cacheBust ? `?t=${Date.now()}` : '';
    try {
      const results = await Promise.allSettled(
        PROVIDERS.map(p =>
          fetch(`${RAW_BASE}/${p}.json${qs}`, { cache: cacheBust ? 'no-store' : 'default' })
            .then(r => r.ok ? r.json() : Promise.reject(new Error(`${p} ${r.status}`)))
            .then(json => [p, json])
        )
      );
      const map = {};
      for (const r of results) {
        if (r.status === 'fulfilled') {
          const [providerID, json] = r.value;
          map[providerID] = json;
        }
      }
      setCatalogs(map);
    } finally {
      setCatalogsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      reloadStatus();
      reloadCatalogs(false);
    }
  }, [isAdmin, reloadStatus, reloadCatalogs]);

  async function runSyncNow() {
    if (!window.confirm(
      'Run the model catalog sync now?\n\n' +
      'This will fetch the latest models from OpenRouter and each provider, then commit any new models or price changes ' +
      'to mchatai-source/main. Safe to run repeatedly (no-op if nothing changed). Takes 30-90 seconds.'
    )) return;

    setRunning(true);
    setRunError(null);
    setLastRunResult(null);
    try {
      const fn = httpsCallable(getFunctions(), 'runModelCatalogSyncOnDemand');
      const result = await fn({ dryRun: false });
      setLastRunResult(result.data);
      await Promise.all([reloadStatus(), reloadCatalogs(true)]);
    } catch (err) {
      setRunError(err.message || String(err));
    } finally {
      setRunning(false);
    }
  }

  // Phase RL — retire one flagged model (calls retireModelInCatalog, commits to mchatai-source).
  async function handleRetire(provider, name) {
    const key = `${provider}/${name}`;
    if (!window.confirm(
      `Retire "${name}" (${provider})?\n\n` +
      'Flips its status to "retired" in mchatai-source/main. It drops out of the app model picker on the next ' +
      'catalog fetch (~5 min). Reversible by editing the provider JSON.'
    )) return;
    setRetiringKey(key);
    setRetireError(null);
    try {
      const fn = httpsCallable(getFunctions(), 'retireModelInCatalog');
      await fn({ provider, name, reason: 'admin review — confirmed broken' });
      setRetiredKeys((prev) => new Set(prev).add(key));
      await reloadStatus();
    } catch (err) {
      setRetireError(`${key}: ${err.message || String(err)}`);
    } finally {
      setRetiringKey(null);
    }
  }

  // Prefer the freshest source: an on-demand run's return value, else the weekly status doc.
  const routeLint = (lastRunResult?.routeLint?.review?.length ? lastRunResult.routeLint : status?.routeLint) || {};
  const reviewItems = routeLint.review || [];
  const autoRetired = routeLint.autoRetired || [];

  if (authLoading) {
    return <div style={pageStyle}><div style={{ color: '#94a3b8', textAlign: 'center', padding: 80 }}>Loading…</div></div>;
  }
  if (!user) {
    return (
      <div style={pageStyle}>
        <div style={{ textAlign: 'center', padding: '80px 24px' }}>
          <h2 style={{ color: '#f8fafc' }}>Sign in required</h2>
          <p style={{ color: '#94a3b8' }}>The model catalog admin page is admin-only.</p>
          <Link to="/" style={{ color: '#3b82f6' }}>← Home</Link>
        </div>
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div style={pageStyle}>
        <div style={{ textAlign: 'center', padding: '80px 24px' }}>
          <h2 style={{ color: '#f8fafc' }}>Access denied</h2>
          <p style={{ color: '#94a3b8' }}>This page is restricted to mChatAI admins.</p>
          <Link to="/account" style={{ color: '#3b82f6' }}>← Account</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <h1 style={{ color: '#f8fafc', fontSize: '2rem', fontWeight: 800, margin: 0 }}>Model Catalog</h1>
          <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
            <Link to="/admin" style={{ color: '#94a3b8', fontSize: '0.85rem' }}>← Admin Dashboard</Link>
            <Link to="/admin/ops" style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Ops →</Link>
          </div>
        </div>
        <p style={{ color: '#64748b', marginBottom: 24, fontSize: '0.9rem' }}>
          Weekly cron <code style={codeStyle}>scheduledModelCatalogSync</code> (Mon 09:00 UTC) pulls fresh model listings from
          OpenRouter + each provider's <code style={codeStyle}>/v1/models</code> endpoint and commits changes to{' '}
          <a href="https://github.com/LawrenceNorman/mchatai-source/tree/main/llm-catalog/providers" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}>
            mchatai-source/llm-catalog/providers
          </a>. The compute proxy re-hydrates from those files every ~5 min, so changes reach users without an app rebuild.
        </p>

        {/* ── Status panel ─────────────────────────────────────────────────── */}
        <StatusPanel status={status} loading={statusLoading} onReload={reloadStatus} />

        {/* ── Stale-model review + Retire ──────────────────────────────────── */}
        <StaleReviewPanel
          items={reviewItems}
          autoRetired={autoRetired}
          onRetire={handleRetire}
          retiringKey={retiringKey}
          retiredKeys={retiredKeys}
          retireError={retireError}
        />

        {/* ── Run now panel ────────────────────────────────────────────────── */}
        <div style={panelStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
            <h2 style={panelTitleStyle}>Run sync now</h2>
            <span style={{ color: '#64748b', fontSize: '0.72rem' }}>
              next scheduled run: {nextScheduledRunLabel()}
            </span>
          </div>
          <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginTop: 0 }}>
            Triggers <code style={codeStyle}>runModelCatalogSyncOnDemand</code> with <code style={codeStyle}>dryRun: false</code> —
            commits any new models or price changes directly to <code style={codeStyle}>mchatai-source/main</code>.
            Idempotent (no-op when nothing changed).
          </p>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 }}>
            <button
              onClick={runSyncNow}
              disabled={running}
              style={primaryBtnStyle(running, '#22c55e')}
            >
              {running ? 'Running… (30-90s)' : 'Run sync now'}
            </button>
            {running && (
              <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                Fetching OpenRouter + provider /models endpoints, computing diffs, committing per-provider files…
              </span>
            )}
          </div>

          {runError && (
            <div style={{ marginTop: 12, padding: 10, background: '#1f0a0a', border: '1px solid #7f1d1d', borderRadius: 6, color: '#fca5a5', fontSize: '0.82rem', fontFamily: 'ui-monospace, monospace' }}>
              Error: {runError}
            </div>
          )}

          {lastRunResult && <RunResultPanel result={lastRunResult} />}
        </div>

        {/* ── Recent additions ─────────────────────────────────────────────── */}
        <div style={panelStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
            <h2 style={panelTitleStyle}>Recent additions</h2>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label style={{ color: '#94a3b8', fontSize: '0.78rem' }}>Window:</label>
              {[7, 30, 90, 365].map(d => (
                <button
                  key={d}
                  onClick={() => setWindowDays(d)}
                  style={pillBtnStyle(windowDays === d)}
                >
                  {d === 365 ? '1y' : `${d}d`}
                </button>
              ))}
              <span style={{ color: '#334155' }}>·</span>
              <select
                value={providerFilter}
                onChange={(e) => setProviderFilter(e.target.value)}
                style={selectStyle}
              >
                <option value="all">All providers</option>
                {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <RecentAdditionsTable
            catalogs={catalogs}
            loading={catalogsLoading}
            windowDays={windowDays}
            providerFilter={providerFilter}
          />
        </div>
      </section>
    </div>
  );
}

// ── Status panel ─────────────────────────────────────────────────────────────

function StatusPanel({ status, loading, onReload }) {
  if (loading && !status) {
    return <div style={panelStyle}><div style={{ color: '#94a3b8' }}>Loading status…</div></div>;
  }
  if (!status) {
    return (
      <div style={panelStyle}>
        <h2 style={panelTitleStyle}>Last sync</h2>
        <p style={{ color: '#94a3b8' }}>No status doc yet — the cron has never run, or Firestore read failed.</p>
      </div>
    );
  }

  const ranAt = status.lastRanAtISO ? new Date(status.lastRanAtISO) : null;
  const ageMs = ranAt ? Date.now() - ranAt.getTime() : null;
  const ageDays = ageMs != null ? Math.floor(ageMs / (1000 * 60 * 60 * 24)) : null;
  const isFailure = status.lastOutcome === 'failure';

  let color = '#22c55e', icon = '🟢';
  if (isFailure || (ageDays != null && ageDays > 14)) { color = '#ef4444'; icon = '🔴'; }
  else if (ageDays != null && ageDays > 7) { color = '#f59e0b'; icon = '🟡'; }

  const ageLabel = ageDays == null ? 'unknown' : ageDays === 0 ? 'today' : `${ageDays} day${ageDays === 1 ? '' : 's'} ago`;

  return (
    <div style={{ ...panelStyle, borderLeft: `4px solid ${color}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <h2 style={panelTitleStyle}>Last sync</h2>
        <button onClick={onReload} style={subtleBtnStyle}>Refresh status</button>
      </div>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 12 }}>
        <Metric label="Status" value={`${icon} ${status.lastOutcome}`} color={color} />
        <Metric label="Last ran" value={ranAt ? ranAt.toLocaleString() : '—'} sub={ageLabel} />
        <Metric label="OpenRouter models seen" value={status.openRouterModelsSeen ?? '—'} />
      </div>

      {isFailure && status.lastError && (
        <div style={{ padding: 10, background: '#1f0a0a', border: '1px solid #7f1d1d', borderRadius: 6, marginBottom: 12 }}>
          <div style={{ color: '#fca5a5', fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem' }}>
            {status.lastError}
          </div>
        </div>
      )}

      {Array.isArray(status.lastCommits) && status.lastCommits.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ color: '#94a3b8', fontSize: '0.72rem', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>
            Commits from last run
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {status.lastCommits.map(c => (
              <a key={c.sha} href={c.commitURL} target="_blank" rel="noopener noreferrer" style={commitRowStyle}>
                <span style={{ color: '#cbd5e1', fontWeight: 600, minWidth: 80 }}>{c.providerID}</span>
                <span style={{ color: '#64748b', fontFamily: 'ui-monospace, monospace', fontSize: '0.75rem' }}>
                  {c.sha?.slice(0, 7)}
                </span>
                <span style={{ color: '#22c55e' }}>+{c.addedCount ?? 0} added</span>
                <span style={{ color: '#f59e0b' }}>{c.priceChangedCount ?? 0} price-changed</span>
                <span style={{ color: '#64748b' }}>{c.retainedCount ?? 0} unchanged</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {status.lastSummaryMarkdown && (
        <details>
          <summary style={{ color: '#94a3b8', fontSize: '0.78rem', cursor: 'pointer', fontWeight: 600 }}>
            Full markdown summary
          </summary>
          <pre style={{
            marginTop: 8, padding: 12, background: '#0b1220', borderRadius: 6,
            color: '#cbd5e1', fontSize: '0.72rem', fontFamily: 'ui-monospace, monospace',
            whiteSpace: 'pre-wrap', maxHeight: 360, overflow: 'auto'
          }}>
            {status.lastSummaryMarkdown}
          </pre>
        </details>
      )}
    </div>
  );
}

// ── Stale-model review + Retire (route-lint) ──────────────────────────────────

const VERDICT_META = {
  'stale-openrouter-id':     { text: 'Stale OpenRouter id', color: '#f59e0b' },
  'heartbeat-failed-review': { text: 'Heartbeat failed',    color: '#ef4444' },
  'audio-review':            { text: 'Audio output',        color: '#a855f7' },
  'route-collision':         { text: 'Route collision',     color: '#ef4444' },
};

function StaleReviewPanel({ items, autoRetired, onRetire, retiringKey, retiredKeys, retireError }) {
  const live = (items || []).filter(
    (it) => !retiredKeys.has(`${it.providerID || it.provider}/${it.name}`)
  );
  const hasData = Array.isArray(items);
  return (
    <div style={{ ...panelStyle, borderLeft: '4px solid #f59e0b' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={panelTitleStyle}>⚠️ Models needing review</h2>
        <span style={{ color: '#64748b', fontSize: '0.72rem' }}>
          {autoRetired?.length ? `${autoRetired.length} auto-retired last run · ` : ''}{live.length} flagged
        </span>
      </div>
      <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginTop: 0 }}>
        Flagged by <code style={codeStyle}>catalog-route-lint</code> as possibly broken — missing from OpenRouter,
        a failed provider-API heartbeat, or ambiguous audio output. These are <strong>not</strong> auto-retired.
        Test the model in the app, then <strong>Retire</strong> the ones that fail (commits <code style={codeStyle}>status: retired</code> to mchatai-source).
      </p>
      {!live.length ? (
        <p style={{ color: '#64748b', fontSize: '0.85rem' }}>
          {hasData && items.length
            ? 'All flagged models handled. 🎉'
            : 'Nothing flagged yet — run a sync (below) to populate this list from the latest route-lint pass.'}
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ color: '#64748b', textAlign: 'left', borderBottom: '1px solid #1e293b' }}>
                <th style={thStyle}>Model</th>
                <th style={thStyle}>Provider</th>
                <th style={thStyle}>Flag</th>
                <th style={thStyle}>Why</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {live.map((it) => {
                const prov = it.providerID || it.provider;
                const key = `${prov}/${it.name}`;
                const meta = VERDICT_META[it.verdict] || { text: it.verdict, color: '#94a3b8' };
                const busy = retiringKey === key;
                return (
                  <tr key={key} style={{ borderBottom: '1px solid #1e293b' }}>
                    <td style={{ ...tdStyle, fontFamily: 'ui-monospace, monospace', color: '#f8fafc' }}>{it.name}</td>
                    <td style={tdStyle}><span style={providerBadgeStyle(prov)}>{prov}</span></td>
                    <td style={tdStyle}><span style={{ color: meta.color, fontWeight: 600 }}>{meta.text}</span></td>
                    <td style={{ ...tdStyle, color: '#94a3b8', maxWidth: 460, fontSize: '0.78rem' }}>{it.reason || ''}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <button onClick={() => onRetire(prov, it.name)} disabled={busy} style={dangerBtnStyle(busy)}>
                        {busy ? 'Retiring…' : 'Retire'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {retireError && (
        <div style={{ marginTop: 12, padding: 10, background: '#1f0a0a', border: '1px solid #7f1d1d', borderRadius: 6, color: '#fca5a5', fontSize: '0.82rem', fontFamily: 'ui-monospace, monospace' }}>
          Retire failed — {retireError}
        </div>
      )}
    </div>
  );
}

// ── Run-now result ───────────────────────────────────────────────────────────

function RunResultPanel({ result }) {
  const providers = result?.providers || {};
  const totalAdded = Object.values(providers).reduce((s, v) => s + (v.addedCount || 0), 0);
  const totalPriceChanged = Object.values(providers).reduce((s, v) => s + (v.priceChangedCount || 0), 0);
  const committedProviders = Object.entries(providers).filter(([, v]) => v.committed);

  return (
    <div style={{ marginTop: 14, padding: 12, background: '#0b1220', border: '1px solid #1e293b', borderRadius: 6 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
        <strong style={{ color: '#22c55e', fontSize: '0.9rem' }}>
          ✓ Run finished
        </strong>
        <span style={{ color: '#64748b', fontSize: '0.72rem' }}>
          {result.openRouterModelsSeen ?? 0} OpenRouter models seen
        </span>
      </div>

      <div style={{ color: '#cbd5e1', fontSize: '0.85rem', marginBottom: 8 }}>
        {totalAdded > 0 || totalPriceChanged > 0
          ? <>Added <strong style={{ color: '#22c55e' }}>{totalAdded}</strong> new model(s), updated <strong style={{ color: '#f59e0b' }}>{totalPriceChanged}</strong> price(s).</>
          : <>No changes — catalog already up to date.</>
        }
      </div>

      {committedProviders.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {committedProviders.map(([providerID, v]) => (
            <a key={v.committed} href={v.commitURL} target="_blank" rel="noopener noreferrer" style={commitRowStyle}>
              <span style={{ color: '#cbd5e1', fontWeight: 600, minWidth: 80 }}>{providerID}</span>
              <span style={{ color: '#64748b', fontFamily: 'ui-monospace, monospace', fontSize: '0.75rem' }}>
                {v.committed?.slice(0, 7)}
              </span>
              <span style={{ color: '#22c55e' }}>+{v.addedCount ?? 0} added</span>
              <span style={{ color: '#f59e0b' }}>{v.priceChangedCount ?? 0} price-changed</span>
            </a>
          ))}
        </div>
      )}

      {result.markdown && (
        <details style={{ marginTop: 8 }}>
          <summary style={{ color: '#94a3b8', fontSize: '0.78rem', cursor: 'pointer', fontWeight: 600 }}>
            Full markdown summary
          </summary>
          <pre style={{
            marginTop: 8, padding: 12, background: '#0f172a', borderRadius: 6,
            color: '#cbd5e1', fontSize: '0.72rem', fontFamily: 'ui-monospace, monospace',
            whiteSpace: 'pre-wrap', maxHeight: 360, overflow: 'auto'
          }}>
            {result.markdown}
          </pre>
        </details>
      )}
    </div>
  );
}

// ── Recent additions ─────────────────────────────────────────────────────────

function RecentAdditionsTable({ catalogs, loading, windowDays, providerFilter }) {
  if (loading) {
    return <div style={{ color: '#94a3b8', padding: 20 }}>Loading catalogs from mchatai-source…</div>;
  }

  const cutoff = Date.now() - windowDays * 24 * 60 * 60 * 1000;
  const rows = [];
  for (const [providerID, json] of Object.entries(catalogs)) {
    if (providerFilter !== 'all' && providerID !== providerFilter) continue;
    for (const m of (json.models || [])) {
      if (!m.addedAt) continue;
      const addedTs = Date.parse(m.addedAt);
      if (isNaN(addedTs) || addedTs < cutoff) continue;
      rows.push({ providerID, ...m, addedTs });
    }
  }
  rows.sort((a, b) => b.addedTs - a.addedTs);

  if (rows.length === 0) {
    return (
      <div style={{ color: '#94a3b8', padding: 20, fontSize: '0.85rem' }}>
        No models added in the last {windowDays} day{windowDays === 1 ? '' : 's'}
        {providerFilter !== 'all' ? ` for ${providerFilter}` : ''}.
        {windowDays < 365 && ' Try a longer window.'}
      </div>
    );
  }

  return (
    <div>
      <div style={{ color: '#64748b', fontSize: '0.78rem', marginBottom: 8 }}>
        Showing {rows.length} model{rows.length === 1 ? '' : 's'} added in the last {windowDays} day{windowDays === 1 ? '' : 's'}.
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #334155', color: '#94a3b8', textAlign: 'left' }}>
              <th style={thStyle}>Added</th>
              <th style={thStyle}>Provider</th>
              <th style={thStyle}>Model</th>
              <th style={thStyle}>Input $/M</th>
              <th style={thStyle}>Output $/M</th>
              <th style={thStyle}>Context</th>
              <th style={thStyle}>Capabilities</th>
              <th style={thStyle}>Discovered via</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.providerID}/${r.name}/${i}`} style={{ borderBottom: '1px solid #1e293b' }}>
                <td style={tdStyle}>
                  <span style={{ color: '#cbd5e1' }}>{r.addedAt}</span>
                </td>
                <td style={tdStyle}>
                  <span style={providerBadgeStyle(r.providerID)}>{r.providerID}</span>
                </td>
                <td style={{ ...tdStyle, fontFamily: 'ui-monospace, monospace' }}>
                  <div style={{ color: '#f8fafc' }}>{r.name}</div>
                  {r.displayName && r.displayName !== r.name && (
                    <div style={{ color: '#64748b', fontSize: '0.72rem' }}>{r.displayName}</div>
                  )}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', color: '#cbd5e1' }}>
                  {r.inputPerMillion != null ? `$${r.inputPerMillion}` : '—'}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', color: '#cbd5e1' }}>
                  {r.outputPerMillion != null ? `$${r.outputPerMillion}` : '—'}
                </td>
                <td style={{ ...tdStyle, color: '#94a3b8' }}>
                  {r.contextTokens ? `${(r.contextTokens / 1000).toFixed(0)}K` : '—'}
                </td>
                <td style={{ ...tdStyle, color: '#94a3b8', fontSize: '0.75rem' }}>
                  {(r.capabilities || []).join(', ') || '—'}
                </td>
                <td style={{ ...tdStyle, color: '#64748b', fontSize: '0.75rem' }}>
                  {(r.discoveredVia || []).join(', ') || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function Metric({ label, value, sub, color }) {
  return (
    <div>
      <div style={{ color: '#64748b', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>{label}</div>
      <div style={{ color: color || '#f8fafc', fontSize: '1.1rem', fontWeight: 600, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ color: '#64748b', fontSize: '0.72rem', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function nextScheduledRunLabel() {
  // Cron is Mon 09:00 UTC. Find the next one from now.
  const now = new Date();
  const next = new Date(now);
  next.setUTCHours(9, 0, 0, 0);
  const dayOfWeek = next.getUTCDay(); // 0=Sun, 1=Mon
  let daysUntilMon = (1 - dayOfWeek + 7) % 7;
  if (daysUntilMon === 0 && next.getTime() <= now.getTime()) daysUntilMon = 7;
  next.setUTCDate(next.getUTCDate() + daysUntilMon);
  return next.toLocaleString();
}

// ── Styles ───────────────────────────────────────────────────────────────────

const pageStyle = { background: '#0f172a', color: '#f8fafc', minHeight: '100vh' };

const panelStyle = {
  background: '#1e293b', borderRadius: 8, padding: 20,
  border: '1px solid #334155', marginBottom: 20
};

const panelTitleStyle = { color: '#f8fafc', fontSize: '1.1rem', fontWeight: 700, margin: 0 };

const codeStyle = {
  background: '#0f172a', padding: '1px 6px', borderRadius: 3,
  fontFamily: 'ui-monospace, monospace', fontSize: '0.82em', color: '#cbd5e1'
};

const thStyle = { padding: '8px 10px', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 };
const tdStyle = { padding: '8px 10px', verticalAlign: 'top' };

const commitRowStyle = {
  display: 'flex', gap: 12, alignItems: 'baseline',
  padding: '6px 10px', background: '#0f172a', borderRadius: 4,
  textDecoration: 'none', fontSize: '0.78rem'
};

const selectStyle = {
  padding: '4px 8px', borderRadius: 4, border: '1px solid #334155',
  background: '#0f172a', color: '#f8fafc', fontSize: '0.78rem',
  fontFamily: 'inherit', outline: 'none', cursor: 'pointer'
};

const subtleBtnStyle = {
  background: 'transparent', color: '#94a3b8', border: '1px solid #334155',
  padding: '4px 10px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 500,
  cursor: 'pointer', fontFamily: 'inherit'
};

function dangerBtnStyle(disabled) {
  return {
    background: disabled ? '#450a0a' : '#7f1d1d', color: disabled ? '#9a5b5b' : '#fecaca',
    border: '1px solid #b91c1c', padding: '4px 12px', borderRadius: 4,
    fontSize: '0.75rem', fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
    fontFamily: 'inherit', whiteSpace: 'nowrap'
  };
}

function pillBtnStyle(active) {
  return {
    padding: '4px 10px', borderRadius: 4, border: '1px solid #334155',
    background: active ? '#3b82f6' : 'transparent',
    color: active ? '#fff' : '#94a3b8',
    fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit'
  };
}

function primaryBtnStyle(disabled, bg = '#3b82f6') {
  return {
    background: bg, color: '#fff', border: 'none', padding: '10px 18px',
    borderRadius: 8, fontWeight: 600, fontSize: '0.9rem',
    cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
    opacity: disabled ? 0.5 : 1
  };
}

function providerBadgeStyle(providerID) {
  const colors = {
    anthropic: '#d97706', openai: '#10a37f', google: '#4285f4',
    mistral: '#ff7000', deepseek: '#6366f1', xai: '#000000', openrouter: '#8b5cf6'
  };
  return {
    background: colors[providerID] || '#475569', color: '#fff',
    padding: '2px 8px', borderRadius: 3, fontSize: '0.7rem', fontWeight: 700
  };
}
